import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Route, withRouter, Switch } from 'react-router-dom'
import MediaQuery from 'react-responsive'
import { fetchAllListings, fetchPublishedListings, fetchSoldListings, fetchOnHoldListings, fetchAutoSoldListings, fetchActionRequiredListings, fetchDisapprovedListings, fetchAwaitingApprovalListings, fetchDraftListings, fetchInterestFromBuyersListings, setSellerId } from 'actions/listingActions'
import buildUrl from '../utils/apiHelpers'
import { myListingsIconUrl } from '../utils/staticAssets'
import ListingsTable from 'components/Table/ListingsTable'
import DropdownFilter from 'components/Listing/DropdownFilter'
import SearchBar from '../components/Listing/SearchBar'
import PinkLinkButton from 'components/atoms/buttons/PinkLinkButton'
import GrayLinkButton from 'components/atoms/buttons/GrayLinkButton'
import NotFound from 'components/Listing/NotFound'

class MyListingsContainer extends Component {
  constructor (props) {
    super(props)

    this.state = {
      noFilteredListings: false,
      searchQuery: '',
      searchIconClicked: false,
      isLoading: false,
      perPage: 100,
      showScrollTopButton: false
    }

    this.handleSelectChange = this.handleSelectChange.bind(this)
    this.handleSearchInputChange = this.handleSearchInputChange.bind(this)
    this.handleSearchSubmit = this.handleSearchSubmit.bind(this)
    this.handleSearchIconClick = this.handleSearchIconClick.bind(this)
    this.handleCrossIconClick = this.handleCrossIconClick.bind(this)
    this.loadListings = this.loadListings.bind(this)
    this.handleScrollToTopClick = this.handleScrollToTopClick.bind(this)
    this.triggerSearch = this.triggerSearch.bind(this)
    this.filterListings = this.filterListings.bind(this)
  }

  componentDidMount () {
    this.setState({ isLoading: true })
    // Store the seller being viewed (admins only) so every dashboard API
    // request is scoped to that seller. Must be dispatched before the first
    // load so the requests pick it up.
    if (this.props.sellerId) {
      this.props.dispatch(setSellerId(this.props.sellerId))
    }
    this.loadListings(1, this.state.perPage)
    window.addEventListener('scroll', () => {
      if (window.pageYOffset > 400) {
        this.setState({ showScrollTopButton: true })
      } else {
        this.setState({ showScrollTopButton: false })
      }
    })
  }

  componentDidUpdate (prevProps) {
    if ((prevProps.listings !== this.props.listings) || (this.state.isLoading && !this.props.success)) {
      this.setState({ isLoading: false })
    }
  }

  loadListings (page, perPage, searchQuery = '', filter = this.getSelectedFilterFromUrl()) {
    const { dispatch } = this.props
    switch (filter) {
      case 'Published':
        return dispatch(fetchPublishedListings(page, perPage, searchQuery))
      case 'All':
        return dispatch(fetchAllListings(page, perPage, searchQuery))
      case 'Sold':
        return dispatch(fetchSoldListings(page, perPage, searchQuery))
      case 'On hold':
        return dispatch(fetchOnHoldListings(page, perPage, searchQuery))
      case 'Auto marked as sold':
        return dispatch(fetchAutoSoldListings(page, perPage, searchQuery))
      case 'Action required':
        return dispatch(fetchActionRequiredListings(page, perPage, searchQuery))
      case 'Disapproved':
        return dispatch(fetchDisapprovedListings(page, perPage, searchQuery))
      case 'Awaiting approval':
        return dispatch(fetchAwaitingApprovalListings(page, perPage, searchQuery))
      case 'Draft':
        return dispatch(fetchDraftListings(page, perPage, searchQuery))
      case 'Interest from buyers':
        return dispatch(fetchInterestFromBuyersListings(page, perPage, searchQuery))
      default:
        return dispatch(fetchAllListings(page, perPage, searchQuery))
    }
  }

  getSelectedFilterFromUrl () {
    const path = this.props.location.pathname
    const filterApplied = path.split('=')[1]

    if (filterApplied) {
      const firstCharacter = filterApplied.charAt(0).toUpperCase()
      const remaining = filterApplied.slice(1).split('-').join(' ')
      return firstCharacter + remaining
    }
    return 'All'
  }

  showPublishedAndOnHoldListings () {
    const statuses = ['Published', 'On hold']
    return this.props.listings.filter(listing => statuses.includes(listing.status))
  }

  showAutoMarkedAsSoldAndStaleListings () {
    const statuses = ['Auto marked as sold', 'Auto marked as stale']
    return this.props.listings.filter(listing => statuses.includes(listing.status))
  }

  filterListings (value) {
    return this.props.listings.filter(listing => listing.status === value)
  }

  showInterestFromBuyersListings () {
    return this.props.listings.filter(listing => listing.interestFromBuyers)
  }

  handleSelectChange (selectedOption) {
    this.setState({ isLoading: true })

    const selectedFilter = selectedOption.value.toLowerCase().split(' ').join('-')
    this.props.history.push(`/filter=${selectedFilter}`)

    this.loadListings(1, this.state.perPage, this.state.searchQuery, selectedOption.value)
  }

  handleSearchIconClick () {
    this.setState({ searchIconClicked: !this.state.searchIconClicked })
  }

  handleCrossIconClick () {
    this.setState({ searchIconClicked: !this.state.searchIconClicked, searchQuery: '' })
    this.triggerSearch('')
  }

  handleSearchInputChange (event) {
    this.setState({ searchQuery: event.target.value })
  }

  handleSearchSubmit (event) {
    event.preventDefault()
    this.triggerSearch(this.state.searchQuery)
  }

  triggerSearch (searchQuery) {
    this.setState({ isLoading: true })
    this.loadListings(1, this.state.perPage, searchQuery)
  }

  handleScrollToTopClick () {
    window.scroll({ top: 0, left: 0, behavior: 'smooth' })
  }

  renderListingsTable (routingProps, listings) {
    const listingsCount = this.props.nbOfListingsCapped
      ? `${this.props.nbOfListings.toLocaleString()}+`
      : this.props.nbOfListings
    return (
      <>
        {!this.state.isLoading && <p className='text-size-l'>{this.getSelectedFilterFromUrl()} ({listingsCount})</p>}
        <ListingsTable
          {...routingProps}
          listings={listings}
          nbOfListings={this.props.nbOfListings}
          hasMore={this.props.hasMore}
          isLoading={this.state.isLoading}
          noFilteredListings={listings.length === 0}
          selectedFilter={this.getSelectedFilterFromUrl()}
          loadMoreListings={this.loadListings}
          perPage={this.state.perPage}
          success={this.props.success}
          searchQuery={this.state.searchQuery}
          p2pSeller={this.props.p2pSeller}
          canBoostListings={this.props.canBoostListings}
        />
      </>
    )
  }

  render () {
    return (
      <div>
        <div className='flex justify-between items-center mt-24 pb-16 [@media(max-width:992px)]:flex-col-reverse [@media(max-width:992px)]:items-start'>
          {this.state.searchIconClicked &&
            <MediaQuery maxWidth={992}>
              <div className='flex items-center justify-between' style={{ width: '100%', marginTop: '16px' }}>
                <SearchBar
                  keyword={this.state.searchQuery}
                  onChange={this.handleSearchInputChange}
                  onSubmit={this.handleSearchSubmit}
                  isLoading={this.state.isLoading}
                  className='seller-listings-search'
                  dataSource='seller-dashboard-listings'
                  minLength={2}
                />
                <span style={{ cursor: 'pointer' }} onClick={this.handleCrossIconClick}>
                  <img src={myListingsIconUrl('cross.svg')} alt='Search' />
                </span>
              </div>
            </MediaQuery>}
          <div className='w-3/5 flex justify-start items-center gap-20 [@media(max-width:992px)]:w-full [@media(max-width:992px)]:justify-between'>
            <DropdownFilter
              onChange={this.handleSelectChange}
              value={this.getSelectedFilterFromUrl()}
            />
            <MediaQuery maxWidth={992}>
              <span style={{ cursor: 'pointer' }} onClick={this.handleSearchIconClick}>
                <img src={myListingsIconUrl('search.svg')} alt='Search' />
              </span>
            </MediaQuery>
            <MediaQuery minWidth={992}>
              <SearchBar
                keyword={this.state.searchQuery}
                onChange={this.handleSearchInputChange}
                onSubmit={this.handleSearchSubmit}
                isLoading={this.state.isLoading}
                className='seller-listings-search'
                dataSource='seller-dashboard-listings'
                minLength={2}
              />
            </MediaQuery>
          </div>
          <div className='[@media(max-width:992px)]:w-full [@media(max-width:992px)]:pb-20 [@media(max-width:992px)]:my-10'>
            <PinkLinkButton className='mr-24 [@media(max-width:992px)]:mr-10' content={['Create listing']} href={buildUrl('/listings/new')} dataSource='seller-dashboard-listings' trackedEvent='create-listing-manual' />
            {!this.props.p2pSeller && <GrayLinkButton content={['Import URL']} href={buildUrl('/listings/import')} dataSource='seller-dashboard-listings' trackedEvent='import-urls' />}
          </div>
        </div>

        <Switch>
          <Route
            path='/'
            exact
            render={(props) => (
              this.renderListingsTable(props, this.props.listings)
            )}
          />

          <Route
            path='/filter=all'
            exact
            render={(props) => (
              this.renderListingsTable(props, this.props.listings)
            )}
          />

          <Route
            path='/filter=published'
            exact
            render={(props) => (
              this.renderListingsTable(props, this.showPublishedAndOnHoldListings())
            )}
          />

          <Route
            path='/filter=on-hold'
            exact
            render={(props) => (
              this.renderListingsTable(props, this.filterListings('On hold'))
            )}
          />

          <Route
            path='/filter=auto-marked-as-sold'
            exact
            render={(props) => (
              this.renderListingsTable(props, this.showAutoMarkedAsSoldAndStaleListings())
            )}
          />

          <Route
            path='/filter=sold'
            exact
            render={(props) => (
              this.renderListingsTable(props, this.filterListings('Sold'))
            )}
          />

          <Route
            path='/filter=draft'
            exact
            render={(props) => (
              this.renderListingsTable(props, this.filterListings('Draft'))
            )}
          />

          <Route
            path='/filter=disapproved'
            exact
            render={(props) => (
              this.renderListingsTable(props, this.filterListings('Disapproved'))
            )}
          />

          <Route
            path='/filter=action-required'
            exact
            render={(props) => (
              this.renderListingsTable(props, this.filterListings('Action required'))
            )}
          />

          <Route
            path='/filter=awaiting-approval'
            exact
            render={(props) => (
              this.renderListingsTable(props, this.filterListings('Awaiting approval'))
            )}
          />

          <Route
            path='/filter=interest-from-buyers'
            exact
            render={(props) => (
              this.renderListingsTable(props, this.showInterestFromBuyersListings())
            )}
          />

          <Route component={NotFound} />
        </Switch>
        {this.state.showScrollTopButton &&
          <button className='vsd-back-to-top-button' onClick={this.handleScrollToTopClick}>
            <span data-icon='k' />
          </button>}
      </div>
    )
  }
}

MyListingsContainer.propTypes = {
  p2pSeller: PropTypes.bool,
  canBoostListings: PropTypes.bool,
  sellerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

const mapStateToProps = (state) => {
  return {
    success: state.listingsReducer.success,
    listings: state.listingsReducer.listings,
    nbOfListings: state.listingsReducer.nbOfListings,
    nbOfListingsCapped: state.listingsReducer.nbOfListingsCapped,
    hasMore: state.listingsReducer.hasMore,
    error: state.listingsReducer.error
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    dispatch
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(MyListingsContainer))
